import { SITE_URL, SITE_NAME, FAVICON_URL, type SeoData } from './seo';
import { spreadsheetArticle } from '@/mocks/articles';

const JSON_LD_MARKER = 'data-readdy-seo-jsonld';

function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description:
      'Explore the AllChinaBuy spreadsheet, product lists, seller resources, QC guides, shipping information and practical buying tips.',
    inLanguage: ['en', 'fr', 'da', 'de'],
  };
}

function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: FAVICON_URL,
  };
}

function articleSchema(seo: SeoData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: spreadsheetArticle.h1,
    description: spreadsheetArticle.metaDescription,
    author: { '@type': 'Organization', name: spreadsheetArticle.author },
    datePublished: spreadsheetArticle.updatedDate,
    dateModified: spreadsheetArticle.updatedDate,
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: FAVICON_URL,
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': seo.canonical },
  };
}

function articleFaqSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: spreadsheetArticle.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };
}

function webPageSchema(seo: SeoData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: seo.title,
    description: seo.description,
    url: seo.canonical,
    inLanguage: seo.lang,
  };
}

function collectionPageSchema(seo: SeoData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: seo.title,
    description: seo.description,
    url: seo.canonical,
    inLanguage: seo.lang,
  };
}

function aboutPageSchema(seo: SeoData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: seo.title,
    description: seo.description,
    url: seo.canonical,
    inLanguage: seo.lang,
  };
}

function contactPageSchema(seo: SeoData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: seo.title,
    description: seo.description,
    url: seo.canonical,
    inLanguage: seo.lang,
  };
}

function breadcrumbSchema(seo: SeoData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: seo.title, item: seo.canonical },
    ],
  };
}

export function getJsonLd(seo: SeoData): object[] {
  if (!seo.indexable) {
    return [];
  }
  switch (seo.type) {
    case 'home':
      return [websiteSchema(), organizationSchema()];
    case 'article':
      return [articleSchema(seo), articleFaqSchema()];
    case 'products':
    case 'categories':
      return [collectionPageSchema(seo), breadcrumbSchema(seo)];
    case 'about':
      return [aboutPageSchema(seo), breadcrumbSchema(seo)];
    case 'contact':
      return [contactPageSchema(seo), breadcrumbSchema(seo)];
    default:
      return [webPageSchema(seo), breadcrumbSchema(seo)];
  }
}

export function applyJsonLd(data: object[]) {
  document.head
    .querySelectorAll(`script[${JSON_LD_MARKER}]`)
    .forEach((el) => el.remove());
  data.forEach((obj) => {
    const el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute(JSON_LD_MARKER, 'true');
    el.textContent = JSON.stringify(obj);
    document.head.appendChild(el);
  });
}